import { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { Icon } from "@/components/ui/Icon";
import { t } from "@/lib/strings";
import { formatLogDate } from "@/lib/format";
import { useMemoSectionHistory, type MemoMutations } from "@/hooks/useMemo";
import type { MemoBlock, MemoEntry, UUID, WorkspaceMember } from "@/lib/types";
import { unitDomId } from "@/lib/memoFocusUnits";
import { buildMemoSections, visibleEntries } from "@/lib/memoSections";
import { historyToEntry, mergeSectionTimeline } from "@/lib/memoSectionTimeline";
import { isHistoryLocked } from "@/lib/memoEditLock";
import { MemoEntryRow } from "@/components/memo/MemoEntryRow";
import { MemoSectionView } from "@/components/memo/MemoSection";
import { MemoSubheadingRow } from "@/components/memo/MemoSubheadingRow";
import { MemoUpdateRow } from "@/components/memo/MemoUpdateRow";
import { MemoMoveMenu } from "@/components/memo/MemoMoveMenu";
import { memoMoveTargets, type MemoMoveSection } from "@/lib/memoMoveTargets";
import { orderMemoLogEntries } from "@/lib/memoLogOrder";

/**
 * A keyboard insert requested from outside (Enter / A while presenting). MemoPage
 * declares the intent; the block that OWNS the focused Zwischenabschnitt executes
 * it, because that is where `addAndEdit` — and the per-block editing state it
 * drives — lives.
 */
export interface PendingInsert {
  blockId: string;
  /** The entry the new one is inserted after; undefined → append at the block's end. */
  afterEntryId?: string;
  asTask: boolean;
  /** 🔴 Distinguishes two consecutive inserts into the SAME unit. Without it the
   * prop is deep-equal on the second Enter and the effect never re-fires. */
  nonce: number;
}

interface Props {
  block: MemoBlock;
  /** The memo this block belongs to — needed to fetch a section's cross-week history. */
  memoId?: UUID;
  /** Attribution is DRAFT-ONLY (see lib/memoAttribution): once the room has seen it, the
   *  line is minutes, not "something that appeared". */
  finalized?: boolean;
  mut: MemoMutations;
  canEdit: boolean;
  members: WorkspaceMember[];
  /** A freshly-created section opens straight into title-rename so it can be named. */
  autoRename?: boolean;
  /** The series' department color for the section color-bar (Protokolle redesign). */
  accentColor: string;
  /** Presentation mode: whether presenting at all. */
  presenting?: boolean;
  /** anchorId of the globally focused unit, or null. */
  focusedAnchorId?: string | null;
  /** True while presenting in `locked` mode — non-focused units dim. */
  dimOthers?: boolean;
  /** May this user rewrite/delete entries carried in from earlier meetings?
   *  (series editors, workspace owner/admin, flow-admin). Server-enforced. */
  mayEditHistory?: boolean;
  /** The unit whose history the presenter deliberately unlocked, or null. */
  unlockedAnchorId?: string | null;
  /** Report a freshly created entry so it counts as "written in this meeting". */
  onEntryCreated?: (entryId: string) => void;
  onFocus?: () => void;
  /** Presentation: make a given entry the focused point (added/clicked-to-edit). */
  onFocusEntry?: (entryId: string) => void;
  /** Reorder this Abschnitt within the protocol (null when at an end). */
  onMoveUp?: (() => void) | null;
  onMoveDown?: (() => void) | null;
  /** Every Abschnitt with its open Zwischenabschnitte — for the per-entry "Verschieben
   *  nach" menu (`lib/memoMoveTargets.ts::memoMoveSections`). */
  sections?: MemoMoveSection[];
  /** Admin: the running-log date dividers become editable (re-date a group). */
  isAdmin?: boolean;
  /** A keyboard insert (Enter / A while presenting). Ignored unless it names THIS block. */
  pendingInsert?: PendingInsert | null;
  /** Fired once the insert has been taken, so MemoPage can clear the intent. */
  onInsertHandled?: () => void;
}

export function MemoBlockView({
  block,
  memoId,
  finalized = false,
  mut,
  canEdit,
  members,
  autoRename,
  accentColor,
  presenting,
  focusedAnchorId = null,
  dimOthers = false,
  mayEditHistory = false,
  unlockedAnchorId = null,
  onEntryCreated,
  onFocus,
  onFocusEntry,
  onMoveUp,
  onMoveDown,
  sections = [],
  isAdmin = false,
  pendingInsert = null,
  onInsertHandled,
}: Props) {
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [dateDraft, setDateDraft] = useState("");
  // Re-date a whole running-log group: the anchor point + the consecutive
  // same-date points beneath it (until the date changes or a sub-heading).
  const redateGroup = (anchorId: string, newDate: string) => {
    const list = orderMemoLogEntries(block.entries);
    const idx = list.findIndex((e) => e.id === anchorId);
    if (idx < 0) return;
    const d = list[idx]?.log_date ?? null;
    for (let j = idx; j < list.length; j++) {
      const e = list[j];
      if (!e || e.kind === "subheading") break;
      // An update carries the date of the day the board event happened — never the
      // group's date. It is neither re-dated nor allowed to END the group: breaking
      // on it would strand the points below it on the old date.
      if (e.kind === "update") continue;
      if (e.log_date !== d) break;
      mut.setEntryLogDate.mutate({ entryId: e.id, logDate: newDate });
    }
  };
  // `headingId` is the section the entry sits in (null = the leading run). A point may move
  // into any Abschnitt or Zwischenabschnitt — its own Abschnitt included; a
  // Zwischenüberschrift only into another Abschnitt (it takes its points along).
  const moveMenuFor = (e: MemoEntry, headingId: string | null) => (
    <MemoMoveMenu
      targets={memoMoveTargets(sections, { kind: e.kind, blockId: block.id, headingId })}
      onMove={(to) => mut.moveEntry.mutate({ entryId: e.id, ...to })}
    />
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [taskHintId, setTaskHintId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [renaming, setRenaming] = useState(!!autoRename && canEdit);
  const [titleDraft, setTitleDraft] = useState(block.title);

  const commitTitle = () => {
    const v = titleDraft.trim();
    if (v && v !== block.title) mut.patchBlock.mutate({ blockId: block.id, title: v });
    setRenaming(false);
  };
  // 🔴 CHRONOLOGICAL, not positional. The running log is dated by definition, and re-dating an
  // entry used to relabel its divider without moving it (27.04 → 29.06 → 20.04, oldest last):
  // `log_date` and `position` were two independent orderings and only `position` drove the
  // render. Position survives as the WITHIN-DAY tiebreak, so a completed to-do still stays
  // where it was written — a run of bullets typed today keeps its typing order, and nothing
  // floats to the top. See lib/memoLogOrder.ts.
  const entries = orderMemoLogEntries(block.entries);
  // Finalized view only: the anchorId of the ONE section whose cross-week history is
  // revealed, or null. A single piece of state means exactly one `useMemoSectionHistory`
  // call for the whole block — never a hook inside the `.map` (rules-of-hooks).
  const [expandedAnchorId, setExpandedAnchorId] = useState<string | null>(null);
  // Ids retired during THIS presentation session — kept visible (greyed) while
  // presenting a draft, so the room watches a point close instead of it vanishing.
  const [retiredThisSession, setRetiredThisSession] = useState<Set<string>>(() => new Set());
  // "This session" means THIS presentation. The view stays mounted (keyed by
  // block.id, not `presenting`) across a presentation ending and restarting, so
  // clear the set on exit — otherwise a point closed in an earlier presentation
  // would wrongly re-surface as visible when a new presentation is entered.
  useEffect(() => {
    if (!presenting) {
      setRetiredThisSession((prev) => (prev.size ? new Set() : prev));
    }
  }, [presenting]);
  const { data: history = [] } = useMemoSectionHistory(
    memoId,
    expandedAnchorId ?? undefined,
    !!expandedAnchorId,
  );
  // Position of each entry in the sorted list — the previous entry's id drives
  // the empty-backspace merge, and grouping by unit breaks the flat map index.
  const indexById = new Map(entries.map((e, i) => [e.id, i]));
  // This block's sections, derived from the block itself by the SAME walk the
  // presentation cursor uses (buildFocusUnits calls buildMemoSections too), so
  // anchorIds match the page-level list — no prop threading needed.
  const blockSections = useMemo(() => buildMemoSections(block), [block]);
  // Which entries are history-protected. Built once so callbacks (empty-backspace)
  // can consult it too, not just the render loop.
  // NOT gated on `presenting` any more: a participant may never rewrite what an
  // earlier meeting recorded, in any mode. Editors/admins are free outside
  // presentation and take the per-unit unlock while screen-sharing.
  const lockedById = useMemo(() => {
    const m = new Map<string, boolean>();
    const byId = new Map(block.entries.map((e) => [e.id, e]));
    for (const section of blockSections) {
      const unitUnlocked = unlockedAnchorId !== null && unlockedAnchorId === section.anchorId;
      // 🔴 The heading is a PROPERTY of the section, not a member of `entries`, so
      // its id has to be added back explicitly — otherwise a carried-forward
      // Zwischenüberschrift would silently lose its history lock.
      const ids = section.heading
        ? [section.heading.id, ...section.entries.map((e) => e.id)]
        : section.entries.map((e) => e.id);
      for (const id of ids) {
        const e = byId.get(id);
        if (!e) continue;
        m.set(
          id,
          isHistoryLocked({ entry: e, mayEditHistory, presenting: !!presenting, unitUnlocked }),
        );
      }
    }
    return m;
  }, [presenting, blockSections, unlockedAnchorId, mayEditHistory, block.entries]);
  const lockOf = (e: MemoEntry) => lockedById.get(e.id) ?? false;

  // 🔴 Positional grouping (Zwischenüberschriften) is NOT expressed per entry any
  // more. `MemoSectionView` puts its members inside a body wrapper offset by
  // SECTION_BODY_INDENT (18) — which is exactly what the old per-point 18 meant —
  // so an entry adding its own 18 on top double-counted the section step.
  //
  // Running-log date dividers: a divider appears above the first point of each
  // new date (consecutive same-date points share one). A sub-heading resets the
  // date context, so the first update under a new heading always shows its date.
  const dividerById = new Map<string, string>();
  {
    let lastDate: string | null = null;
    for (const e of entries) {
      if (e.kind === "subheading") {
        lastDate = null;
        continue;
      }
      // 🔴 An update carries its own date INLINE (`⤷ 09.07.2026 · MKO`), so it must
      // neither open a running-log divider nor ADVANCE the date context: a comment
      // written today, landing under a point written three weeks ago, would
      // otherwise split that old date group in two — the points below it would each
      // sprout a spurious divider repeating the date they already sit under.
      if (e.kind === "update") continue;
      if (e.log_date && e.log_date !== lastDate) {
        dividerById.set(e.id, e.log_date);
        lastDate = e.log_date;
      }
    }
  }
  // The entry an empty-Backspace merge falls back to. An update has no editor, so
  // focusing it would swallow the keystroke — skip to the previous real line.
  const prevEditableId = (i: number): string | undefined => {
    for (let j = i - 1; j >= 0; j--) {
      const p = entries[j];
      if (p && p.kind !== "update") return p.id;
    }
    return undefined;
  };

  const addAndEdit = async (asTask = false, afterEntryId?: string) => {
    const created = await mut.addEntry.mutateAsync({ blockId: block.id, afterEntryId });
    onEntryCreated?.(created.id);
    setEditingId(created.id);
    setTaskHintId(asTask ? created.id : null);
    // While presenting, spotlight the freshly-added point (it becomes the focus,
    // and stays focused after you finish editing — not snapping back).
    if (presenting) onFocusEntry?.(created.id);
  };
  const addSubheading = async (afterEntryId?: string) => {
    // A sub-heading (Zwischenüberschrift) is a persistent group label — created
    // straight into rename, never a to-do, and NOT a presentation focus stop.
    const created = await mut.addEntry.mutateAsync({
      blockId: block.id,
      afterEntryId,
      kind: "subheading",
    });
    onEntryCreated?.(created.id);
    setEditingId(created.id);
    setTaskHintId(null);
  };

  // A keyboard insert while presenting (Enter → Eintrag, A → Aufgabe). MemoPage
  // owns the keyboard and declares the intent; the block owning the focused
  // Zwischenabschnitt executes it through the SAME `addAndEdit` the hover
  // "+ Eintrag" / "+ Aufgabe" pills use — so the ghost-row cleanup, the
  // autoMention @-priming and focus-follows-add all come along unchanged.
  //
  // Keyed on the NONCE, not the object: two Enters into the same unit produce an
  // otherwise identical intent, and the second must still fire.
  const insertNonce = pendingInsert?.blockId === block.id ? pendingInsert.nonce : null;
  // `addAndEdit` closes over `block`/`mut` and is rebuilt every render, so it must
  // not be a dep; read the payload and the callback through refs instead.
  const pendingRef = useRef(pendingInsert);
  pendingRef.current = pendingInsert;
  const addAndEditRef = useRef(addAndEdit);
  addAndEditRef.current = addAndEdit;
  const onInsertHandledRef = useRef(onInsertHandled);
  onInsertHandledRef.current = onInsertHandled;
  useEffect(() => {
    if (insertNonce === null) return;
    const p = pendingRef.current;
    if (!p) return;
    // Clear the intent BEFORE the await: `addAndEdit` round-trips to the server,
    // and a re-render in between must not find the intent still standing.
    onInsertHandledRef.current?.();
    void addAndEditRef.current(p.asTask, p.afterEntryId);
  }, [insertNonce]);

  // Closing a sub-heading cascades to its member points (backend), so confirm
  // first. Un-closing goes straight through (points aren't revived).
  const [closeHeading, setCloseHeading] = useState<MemoEntry | null>(null);
  const openMemberCount = (headingId: string): number => {
    const idx = entries.findIndex((e) => e.id === headingId);
    if (idx < 0) return 0;
    let n = 0;
    for (let j = idx + 1; j < entries.length; j++) {
      const ej = entries[j];
      if (!ej || ej.kind === "subheading") break;
      // An update is not a member POINT — it must not inflate the "…und ihre {n}
      // offenen Punkte" count in the close-heading confirmation.
      if (ej.kind === "update") continue;
      if (!ej.retired) n += 1;
    }
    return n;
  };
  const toggleRetireHeading = (e: MemoEntry) => {
    if (e.retired) mut.retireEntry.mutate({ entryId: e.id, retired: false });
    else setCloseHeading(e);
  };
  const emptyBackspace = async (entryId: string, prevId: string | undefined) => {
    await mut.deleteEntry.mutateAsync(entryId);
    // Never focus a history-locked entry: mounting its editor would let a blur or
    // Enter silently rewrite an earlier meeting's text with no unlock.
    setEditingId(prevId && !lockedById.get(prevId) ? prevId : null);
  };

  // The author of a board-sourced update line: the person who wrote the comment /
  // moved the Frist. Attribution is why the line is read-only, so a member we cannot
  // resolve still says something rather than rendering a nameless line.
  const authorNameOf = (e: MemoEntry): string => {
    const m = members.find((x) => x.user_id === e.created_by);
    return m ? (m.display_name ?? m.username ?? m.email) : t.memoUpdateUnknownAuthor;
  };

  // Presentation focus/dim now lives on the FOCUS UNIT container (below), never
  // on an individual point — so no per-point ring and no per-point subduing.
  const row = (e: MemoEntry, prevId: string | undefined, headingId: string | null) => (
    <MemoEntryRow
      key={e.id}
      entry={e}
      mut={mut}
      canEdit={canEdit}
      members={members}
      editing={editingId === e.id}
      taskHint={taskHintId === e.id}
      historyLocked={lockOf(e)}
      finalized={finalized}
      mayEditHistory={mayEditHistory}
      onStartEdit={() => {
        setEditingId(e.id);
        if (presenting) onFocusEntry?.(e.id);
      }}
      onStopEdit={() => setEditingId((cur) => (cur === e.id ? null : cur))}
      // The promise goes to the row: it ignores its own removal blur while the delete runs, and
      // settles a refused delete (which the hook reports) instead of leaving it unhandled.
      onEmptyBackspace={() => emptyBackspace(e.id, prevId)}
      moveMenu={moveMenuFor(e, headingId)}
      onToggleRetire={(id, retired) => {
        if (retired) {
          setRetiredThisSession((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
          });
        }
      }}
    />
  );

  const hasEntries = entries.length > 0;

  const colorBar = (
    <span
      style={{
        width: 6,
        height: 18,
        borderRadius: 2,
        background: hasEntries ? accentColor : "var(--hairline-strong)",
        flexShrink: 0,
      }}
    />
  );

  const titleNode = renaming ? (
    <input
      autoFocus
      value={titleDraft}
      onChange={(e) => setTitleDraft(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={commitTitle}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commitTitle();
        } else if (e.key === "Escape") {
          setRenaming(false);
        }
      }}
      style={{
        fontFamily: "var(--font-display)",
        fontSize: 17,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: "var(--ink-1)",
        background: "var(--surface-soft)",
        border: "1px solid var(--hairline)",
        borderRadius: 6,
        padding: "2px 8px",
        minWidth: 0,
      }}
    />
  ) : (
    <h3
      onClick={() => {
        if (canEdit && !presenting) {
          setTitleDraft(block.title);
          setRenaming(true);
        }
      }}
      title={canEdit && !presenting ? t.memoRenameBlock : undefined}
      style={{
        fontFamily: "var(--font-display)",
        fontSize: 17,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: hasEntries ? "var(--ink-1)" : "var(--ink-2)",
        margin: 0,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: canEdit ? "text" : "default",
      }}
    >
      {block.title}
    </h3>
  );

  // Structure is frozen while presenting: rename / delete / reorder are
  // destructive and structural. Appending stays allowed.
  //
  // 🔴 NOT `canEdit`. On a DRAFT canEdit means EVERY PARTICIPANT — and deleting an Abschnitt
  // hard-deletes every point in it, including everyone else's and every line carried forward
  // from an earlier meeting. It was a straight bypass of the history lock: the same person is
  // refused when deleting ONE carried line, but the section holding twenty of them was open.
  // `mayEditHistory` IS can_edit_finalized, which is the right the server now demands
  // (memo_access.require_trusted_edit). The server is the boundary; this only stops the UI
  // offering an act it will refuse.
  const deleteButton =
    canEdit && mayEditHistory && !presenting ? (
      <button
        type="button"
        title="Abschnitt löschen"
        onClick={() => setDeleteOpen(true)}
        className="flow-memo-block-del"
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 2,
          flexShrink: 0,
        }}
      >
        <Icon name="trash" size={13} color="var(--ink-4)" />
      </button>
    ) : null;

  // Reorder the Abschnitt within the protocol. Hover-revealed like delete; a
  // disabled arrow (dimmed) marks the top/bottom section.
  const moveBtnStyle = (enabled: boolean): React.CSSProperties => ({
    background: "transparent",
    border: "none",
    cursor: enabled ? "pointer" : "default",
    padding: 2,
    flexShrink: 0,
    opacity: enabled ? 1 : 0.3,
  });
  const moveButtons =
    canEdit && !presenting ? (
      <>
        <button
          type="button"
          title="Abschnitt nach oben"
          disabled={!onMoveUp}
          onClick={() => onMoveUp?.()}
          className="flow-memo-block-del"
          style={moveBtnStyle(!!onMoveUp)}
        >
          <Icon name="chevUp" size={15} color="var(--ink-4)" />
        </button>
        <button
          type="button"
          title="Abschnitt nach unten"
          disabled={!onMoveDown}
          onClick={() => onMoveDown?.()}
          className="flow-memo-block-del"
          style={moveBtnStyle(!!onMoveDown)}
        >
          <Icon name="chevDown" size={15} color="var(--ink-4)" />
        </button>
      </>
    ) : null;

  const confirmDialog = canEdit ? (
    <ConfirmDialog
      open={deleteOpen}
      onOpenChange={setDeleteOpen}
      title={t.memoDeleteBlockTitle}
      message={t.memoDeleteBlockMessage.replace("{name}", block.title)}
      confirmLabel={t.memoDeleteBlock}
      tone="danger"
      onConfirm={() => {
        mut.deleteBlock.mutate(block.id);
        setDeleteOpen(false);
      }}
    />
  ) : null;

  // Presentation dimming lives on each FOCUS UNIT container (see the render
  // below), not on the whole section — a section can hold several
  // Zwischenabschnitte. The section header dims only when no unit inside it is
  // the focused one. Opacity only: a blur() filter was a costly repaint on
  // every cursor move, which is what made stepping feel sluggish.
  const blockHasFocus = blockSections.some((s) => s.anchorId === focusedAnchorId);
  const headerDim = dimOthers && !blockHasFocus;
  const dimStyle: React.CSSProperties = {
    transition: "opacity 160ms ease",
    opacity: headerDim ? 0.35 : 1,
    pointerEvents: headerDim ? "none" : undefined,
  };
  // The NORMAL section must not dim as a whole — its units dim themselves, and a
  // section-wide opacity would compound with theirs. Only its header dims.
  const presentStyle: React.CSSProperties = { transition: "opacity 160ms ease" };
  // A collapsed/empty section IS its own focus unit, so it dims and anchors as one.
  // It is NOT a MemoSectionView (the compact row has its own geometry), so the DOM
  // id the presentation cursor scrolls to is still stamped by hand here.
  const emptyUnit = blockSections[0] ?? null;
  const emptySectionStyle: React.CSSProperties = { ...dimStyle, scrollMarginTop: 80 };

  // Hover inserter: a floating "+ Eintrag / + Aufgabe" pill in the gap under an
  // entry, inserting a new point/task right after it. Available while presenting
  // too (you take notes live) — dimmed sections have pointer-events:none, so it
  // only reaches the focused/interactive section.
  const inserter = (afterEntryId: string) =>
    canEdit ? (
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          // Sit in the gap BELOW the entry (mostly clear of it) rather than
          // overlapping its last line, which blocked content + made editing
          // harder — especially on the emphasized point in presentation.
          bottom: -20,
          zIndex: 1,
          display: "flex",
          justifyContent: "center",
          // The full-width band never catches clicks; only the pill does, and
          // only while revealed (see .flow-memo-inserter-pill in index.css).
          pointerEvents: "none",
        }}
      >
        <div
          className="flow-memo-inserter-pill"
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            background: "var(--surface)",
            border: "1px solid var(--hairline)",
            borderRadius: 999,
            padding: "3px 12px",
            boxShadow: "0 2px 8px rgba(13,62,99,0.10)",
          }}
        >
          <button
            type="button"
            onClick={() => void addAndEdit(false, afterEntryId)}
            style={addEntryBtn}
          >
            {t.memoAddEntry}
          </button>
          <span style={{ width: 1, height: 12, background: "var(--hairline)" }} />
          <button
            type="button"
            onClick={() => void addAndEdit(true, afterEntryId)}
            style={addEntryBtn}
          >
            {t.memoAddTask}
          </button>
          <span style={{ width: 1, height: 12, background: "var(--hairline)" }} />
          <button
            type="button"
            onClick={() => void addSubheading(afterEntryId)}
            style={addEntryBtn}
          >
            {t.memoAddSubheading}
          </button>
        </div>
      </div>
    ) : null;

  const focusButton =
    presenting && onFocus ? (
      <button
        type="button"
        title="Diesen Abschnitt fokussieren"
        // Focus stays on the document: MemoPage's presentation container prevents
        // the default mousedown on every button while presenting, so the keyboard
        // insert loop survives a click here.
        onClick={onFocus}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 2,
          flexShrink: 0,
        }}
      >
        <Icon name={blockHasFocus ? "eye" : "expand"} size={14} color="var(--ink-4)" />
      </button>
    ) : null;

  if (!hasEntries) {
    // Collapsed empty section: a single compact row instead of a full-height
    // section — the color bar + (still inline-renameable) title on the left,
    // the same add-entry action on the right.
    return (
      <section {...(emptyUnit ? { id: unitDomId(emptyUnit) } : {})} style={emptySectionStyle}>
        {confirmDialog}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background: "var(--surface-soft)",
            border: "1px solid var(--hairline)",
            borderRadius: 10,
            padding: "14px 16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {colorBar}
            {titleNode}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {focusButton}
            {moveButtons}
            {deleteButton}
            {canEdit ? (
              <>
                <button type="button" onClick={() => void addAndEdit(false)} style={emptyAddBtn}>
                  {t.memoAddEntry}
                </button>
                <button type="button" onClick={() => void addAndEdit(true)} style={emptyAddBtn}>
                  {t.memoAddTask}
                </button>
                <button type="button" onClick={() => void addSubheading()} style={emptyAddBtn}>
                  {t.memoAddSubheading}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section style={presentStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, ...dimStyle }}>
        {colorBar}
        {titleNode}
        {focusButton}
        {moveButtons}
        {deleteButton}
      </div>

      {confirmDialog}
      {closeHeading ? (
        <ConfirmDialog
          open={!!closeHeading}
          onOpenChange={(o) => {
            if (!o) setCloseHeading(null);
          }}
          title={t.memoSubheadingCloseTitle.replace(
            "{name}",
            closeHeading.body.trim() || t.memoSubheadingPlaceholder,
          )}
          message={t.memoSubheadingCloseMessage.replace(
            "{n}",
            String(openMemberCount(closeHeading.id)),
          )}
          confirmLabel={t.memoSubheadingCloseConfirm}
          tone="danger"
          onConfirm={() => {
            mut.retireEntry.mutate({ entryId: closeHeading.id, retired: true });
            setCloseHeading(null);
          }}
        />
      ) : null}

      <div>
        {blockSections.map((section) => {
          const dim = dimOthers && section.anchorId !== focusedAnchorId;
          // Retired points collapse by default in BOTH drafts and finalized memos; the
          // reveal control appears only where something is closed in THIS section.
          // Expanding additionally reconstructs the section's cross-week history.
          const isExpanded = expandedAnchorId === section.anchorId;
          // Show the reveal where something is closed here OR where the section has a
          // past (all-open this week, closures in prior weeks — the `frühere Punkte`
          // case). historyCount is the denormalised backend signal.
          const showReveal = section.hasClosedHere || section.historyCount > 0;
          // Expanding reconstructs the section's cross-week arc in BOTH draft and
          // finalized — its own closed lines merged with prior meetings' closures.
          // Historical entries are marked is_historical (read-only + dated badge).
          const rows = isExpanded
            ? mergeSectionTimeline(
                section.entries,
                history.map((h) => historyToEntry(h, block.id)),
              )
            : visibleEntries(section, {
                expanded: false,
                presenting: !!presenting,
                finalized,
                retiredThisSession,
              });
          return (
            <MemoSectionView
              key={section.anchorId}
              anchorId={section.anchorId}
              dim={dim}
              reveal={
                showReveal ? { expanded: isExpanded, closedCount: section.closedCount } : null
              }
              onToggleReveal={() => setExpandedAnchorId(isExpanded ? null : section.anchorId)}
              heading={
                section.heading ? (
                  // NOTE: a Zwischenüberschrift carries no attribution yet. It is arguably
                  // ghost content too — someone added it mid-week — but it is structure
                  // rather than a claim, and its row has no right margin to hang an avatar
                  // in. Points first; raise this if a stray heading ever confuses a room.
                  //
                  // 🔴 The slot wrapper is what carries the hover inserter. The heading used
                  // to be an ordinary row in the entry loop and got its pill from there; now
                  // that it is a section PROPERTY it has to be re-hung here, or a section
                  // holding only a Zwischenüberschrift offers no way to add the first point
                  // under it at all.
                  <div className="flow-memo-slot" style={{ position: "relative" }}>
                    <MemoSubheadingRow
                      entry={section.heading}
                      canEdit={canEdit}
                      historyLocked={lockOf(section.heading)}
                      mayEditHistory={mayEditHistory}
                      editing={editingId === section.heading.id}
                      onStartEdit={() => setEditingId(section.heading!.id)}
                      onStopEdit={() =>
                        setEditingId((cur) => (cur === section.heading!.id ? null : cur))
                      }
                      onCommit={(body) => {
                        if (body !== section.heading!.body)
                          mut.patchEntry.mutate({ entryId: section.heading!.id, body });
                      }}
                      onEnterNext={() => void addAndEdit(false, section.heading!.id)}
                      onToggleRetire={() => toggleRetireHeading(section.heading!)}
                      onDelete={() => mut.deleteEntry.mutate(section.heading!.id)}
                      moveMenu={moveMenuFor(section.heading, null)}
                    />
                    {inserter(section.heading.id)}
                  </div>
                ) : null
              }
            >
              {rows.map((e) => {
                const i = indexById.get(e.id) ?? 0;
                return (
                  <div key={e.id} className="flow-memo-slot" style={{ position: "relative" }}>
                    {e.kind === "update" ? (
                      // Board activity, materialised under the to-do it reports on.
                      // Indented one step further than its parent point, and NEVER
                      // click-to-edit: the line is attributed (see MemoUpdateRow).
                      <div style={{ paddingLeft: 16 }}>
                        <MemoUpdateRow
                          entry={e}
                          authorName={authorNameOf(e)}
                          canEdit={canEdit}
                          onDelete={(id) => mut.deleteEntry.mutate(id)}
                        />
                      </div>
                    ) : (
                      <div>
                        {dividerById.has(e.id) ? (
                          <div style={{ marginTop: 12, marginBottom: 1 }}>
                            {isAdmin && editingDateId === e.id ? (
                              <span
                                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                              >
                                <input
                                  type="date"
                                  autoFocus
                                  // Draft-only: neither typing (which briefly forms a
                                  // valid date) nor the native calendar's month arrows
                                  // commit. Enter or the ✓ commits; Esc cancels.
                                  value={dateDraft}
                                  onChange={(ev) => setDateDraft(ev.target.value)}
                                  onKeyDown={(ev) => {
                                    if (ev.key === "Enter" && dateDraft) {
                                      redateGroup(e.id, dateDraft);
                                      setEditingDateId(null);
                                    } else if (ev.key === "Escape") {
                                      setEditingDateId(null);
                                    }
                                  }}
                                  style={{
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    border: "1px solid var(--hairline-strong)",
                                    borderRadius: 6,
                                    padding: "2px 6px",
                                    background: "var(--surface)",
                                    color: "var(--ink-1)",
                                  }}
                                />
                                <button
                                  type="button"
                                  title="Übernehmen"
                                  disabled={!dateDraft}
                                  onClick={() => {
                                    if (dateDraft) {
                                      redateGroup(e.id, dateDraft);
                                      setEditingDateId(null);
                                    }
                                  }}
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: dateDraft ? "pointer" : "default",
                                    padding: 2,
                                    lineHeight: 0,
                                  }}
                                >
                                  <Icon name="check" size={15} color="var(--ink-2)" />
                                </button>
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={
                                  isAdmin
                                    ? () => {
                                        setDateDraft(dividerById.get(e.id) ?? "");
                                        setEditingDateId(e.id);
                                      }
                                    : undefined
                                }
                                title={isAdmin ? "Datum ändern" : undefined}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  padding: 0,
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  letterSpacing: "0.02em",
                                  color: "var(--ink-3)",
                                  cursor: isAdmin ? "pointer" : "default",
                                }}
                              >
                                {formatLogDate(dividerById.get(e.id)!)}
                              </button>
                            )}
                          </div>
                        ) : null}
                        {row(e, prevEditableId(i), section.heading?.id ?? null)}
                      </div>
                    )}
                    {e.is_historical ? null : inserter(e.id)}
                  </div>
                );
              })}
            </MemoSectionView>
          );
        })}
      </div>
      {/* The always-visible bottom "+ Eintrag / + Aufgabe" footer was removed —
          the floating hover inserter (rendered after every entry, incl. the
          last) already covers add-at-the-end, so both together were redundant. */}
    </section>
  );
}

const addEntryBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--ink-4)",
  fontSize: 12.5,
  cursor: "pointer",
  padding: "2px 0",
};

const emptyAddBtn: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  background: "transparent",
  border: "1px solid var(--hairline-strong)",
  borderRadius: 8,
  padding: "5px 10px",
  fontSize: 12.5,
  color: "var(--ink-3)",
  cursor: "pointer",
  whiteSpace: "nowrap",
};
